import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FiArrowLeft, FiHome } from 'react-icons/fi';

export default function FacilityNew({ organizations = [], user }) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    description: '',
    organization_id: '',
    care_type: 'assisted_living',
    address: '',
    published: false,
    main_photo: null
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    post('/facility_owner/facilities');
  };

  const handleFileChange = (e) => {
    setData('main_photo', e.target.files[0]);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/facilities">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Facilities
              </Link>
            </Button>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Add New Facility</h1>
                <p className="text-gray-500">Create a new assisted living or memory care facility</p>
              </div>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Facility Information</CardTitle>
              <CardDescription>Enter the details of your new facility</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Facility Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={data.name}
                      onChange={e => setData('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={data.description}
                      onChange={e => setData('description', e.target.value)}
                      rows={5}
                      className={errors.description ? 'border-red-500' : ''}
                    />
                    {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="organization_id">Organization</Label>
                    <Select
                      value={data.organization_id}
                      onValueChange={(value) => setData('organization_id', value)}
                    >
                      <SelectTrigger className={errors.organization_id ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id.toString()}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.organization_id && <p className="text-red-500 text-sm mt-1">{errors.organization_id}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="care_type">Care Type</Label>
                    <Select
                      value={data.care_type}
                      onValueChange={(value) => setData('care_type', value)}
                    >
                      <SelectTrigger className={errors.care_type ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select care type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="assisted_living">Assisted Living</SelectItem>
                        <SelectItem value="memory_care">Memory Care</SelectItem>
                        <SelectItem value="independent_living">Independent Living</SelectItem>
                        <SelectItem value="nursing_home">Nursing Home</SelectItem>
                        <SelectItem value="continuing_care">Continuing Care</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.care_type && <p className="text-red-500 text-sm mt-1">{errors.care_type}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      type="text"
                      value={data.address}
                      onChange={e => setData('address', e.target.value)}
                      className={errors.address ? 'border-red-500' : ''}
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="main_photo">Main Photo</Label>
                    <Input
                      id="main_photo"
                      type="file"
                      onChange={handleFileChange}
                      className={errors.main_photo ? 'border-red-500' : ''}
                    />
                    {errors.main_photo && <p className="text-red-500 text-sm mt-1">{errors.main_photo}</p>}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="published"
                      checked={data.published}
                      onCheckedChange={(checked) => setData('published', checked)}
                    />
                    <Label htmlFor="published">Publish this facility (make it visible to the public)</Label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" asChild>
                    <Link href="/facility_owner/facilities">Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    Create Facility
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
